/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { DocumentVersionFlag } from './documentVersionFlags/documentVersionFlags';
import { IPearlLibrary, PearlUser } from './Login/userSlice';

const DEV_LIBRARY_ID = '17ba5576-0380-4dca-8ae7-f85b3130d9a8'; // For localhost and -dev and -test
const DEMORG_LIBRARY_ID = 'c0e98c55-44c2-41e6-ab7e-d8b4baa54721'; // 540 published docs
const PROVIDENCE_LIBRARY_ID = 'ab30fb84-7e12-4b00-bb94-31d6a982d46c'; // Providence 1299 published docs
const PROVIDENCE_ONLY_LIBRARY_ID = 'dd0608cf-03d4-4a68-9203-36bd6bc3c1c2'; // Providence-only 1 published docs
const MUSC_ONLY_LIBRARY_ID = 'b028c82f-5932-459e-9f93-f439f6dac6ef'; // MUSC-only 0 published docs
const QA_LIBRARY_ID = '86497048-855a-4b3b-9409-96af7b977c13'; // QA 0 published docs
const DEMO_LIBRARY_ID = 'adcb0849-2df7-4a5c-ac22-ab4581a5cbf6'; // Demo 1 published docs
const MEAT_LIBRARY_ID = '04062d73-f104-4116-94af-adb84c2a453a'; // MEAT library for Carebotics

export type LibraryDetailsType = {
  name: string;
  flag?: DocumentVersionFlag;
  badgeName?: string;
};
/** Note when shown the name will be suffixed with " Library" */
export const libraryIdToDetailsMap: { [key: string]: LibraryDetailsType } = {
  [DEV_LIBRARY_ID]: { name: 'Development', badgeName: 'Internal for Development' },
  [DEMORG_LIBRARY_ID]: { name: 'Demo Organization', flag: DocumentVersionFlag.demorg_library },
  [PROVIDENCE_LIBRARY_ID]: { name: 'Providence' },
  [PROVIDENCE_ONLY_LIBRARY_ID]: {
    name: 'Providence Only',
    flag: DocumentVersionFlag.providence_only_library,
    badgeName: 'Internal to Providence',
  },
  [MUSC_ONLY_LIBRARY_ID]: {
    name: 'MUSC',
    badgeName: 'Medical University of South Carolina',
    flag: DocumentVersionFlag.musc_only_library,
  },
  [QA_LIBRARY_ID]: { name: 'QA' },
  [DEMO_LIBRARY_ID]: { name: 'Demo', flag: DocumentVersionFlag.demo_library },
  [MEAT_LIBRARY_ID]: {
    name: 'MEAT',
    badgeName: 'Health Endeavors Carebotics: Monitor, Evaluate, Assess/Address, Treat',
  },
} as const;

export function documentVersionLibraryToStr(library: IPearlLibrary | undefined) {
  if (!library) return;
  if (library?.name) return library.name;
  const details = libraryIdToDetailsMap[library.id];
  if (details) return details.name;
  return 'Unknown';
}

export function libraryWithDisplayName(library: IPearlLibrary): IPearlLibrary {
  return {
    ...library,
    name: documentVersionLibraryToStr(library),
  };
}

export function getDefaultWritableLibrary(pearlUserInfo: PearlUser | undefined): IPearlLibrary | undefined {
  if (!pearlUserInfo) return;
  return (
    pearlUserInfo.writableLibraries?.find((library) => library.id === pearlUserInfo.defaultLibraryId) ||
    (pearlUserInfo.defaultLibraryId ? { id: pearlUserInfo.defaultLibraryId } : undefined) ||
    pearlUserInfo.writableLibraries?.[0]
  );
}

export function documentVersionLibraryToDetails(library: IPearlLibrary | undefined) {
  if (!library) return;
  return libraryIdToDetailsMap[library.id];
}
